import { useQuery, useQueryClient } from 'react-query';
import { dashboardAPI } from '../services/api';

/**
 * Custom hook for dashboard data management
 * Provides dashboard data, loading state, error state, and refetch capabilities
 */
export const useDashboard = () => {
  const queryClient = useQueryClient();

  const {
    data: dashboardData,
    isLoading,
    error,
    refetch,
  } = useQuery('dashboard', dashboardAPI.getDashboardData, {
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  // Function to manually invalidate dashboard cache
  const invalidateDashboard = () => {
    console.log('Invalidating dashboard cache...');
    queryClient.invalidateQueries('dashboard');
  };

  // Function to refetch dashboard data
  const refetchDashboard = () => {
    console.log('Refetching dashboard data...');
    return refetch();
  };

  return {
    dashboardData,
    isLoading,
    error,
    refetch: refetchDashboard,
    invalidate: invalidateDashboard,
  };
};

/**
 * Hook to get dashboard refetch function for use in other components
 * This allows components to trigger dashboard updates without direct access to the dashboard
 */
export const useDashboardRefetch = () => {
  const queryClient = useQueryClient();

  const invalidateDashboard = () => {
    console.log('Invalidating dashboard cache from external component...');
    queryClient.invalidateQueries('dashboard');
  };

  // Function to remove stale dashboard data
  const removeDashboardData = () => {
    console.log('Removing dashboard data from cache...');
    queryClient.removeQueries('dashboard');
  };

  return {
    invalidateDashboard,
    removeDashboardData,
  };
};
